import React, { useMemo } from "react";
import { Button, Table, Tag } from "antd";
import type { ColumnsType } from "antd/es/table";
import useLang from "../../../hooks/useLang";
import { OrderStatusHistoryRow } from "../../../api/bulk-order-operations";
import { localStatusLabel } from "./statuses";

interface HistoryTableProps {
	rows: OrderStatusHistoryRow[];
	loading: boolean;
	onDelete: (row: OrderStatusHistoryRow) => void;
}

const formatDateTime = (value: string) => {
	const date = new Date(value);
	return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};

const HistoryTable = ({ rows, loading, onDelete }: HistoryTableProps) => {
	const { strings } = useLang("bulkOrderOperations");
	const copy = strings?.historyManagement;

	const columns = useMemo<ColumnsType<OrderStatusHistoryRow>>(
		() => [
			{
				title: copy?.colDate ?? "Date",
				dataIndex: "statusDate",
				key: "statusDate",
				render: (value: string, row) => (
					<span className="flex items-center gap-2">
						{formatDateTime(value)}
						{row.isLatest && (
							<Tag color="blue">{copy?.latest ?? "Latest"}</Tag>
						)}
					</span>
				),
			},
			{
				title: copy?.colStatus ?? "Status",
				dataIndex: "statusId",
				key: "statusId",
				// statusLabel is dbo.Status.Description; fall back to this screen's own
				// names, then to the bare id so a row is never rendered as blank.
				render: (statusId: number, row) => (
					<span>
						{row.statusLabel ??
							localStatusLabel(statusId) ??
							`#${statusId}`}{" "}
						<span className="text-gray-400">({statusId})</span>
					</span>
				),
			},
			{
				title: copy?.colUser ?? "Responsible",
				dataIndex: "userName",
				key: "userName",
				render: (userName: string | null, row) => (
					<span>
						{userName?.trim() || `#${row.userId}`}
						{row.userRoleDescription && (
							<span className="text-gray-400">
								{" "}
								· {row.userRoleDescription}
							</span>
						)}
					</span>
				),
			},
			{
				title: copy?.colComment ?? "Description",
				dataIndex: "statusChangeComment",
				key: "statusChangeComment",
				render: (comment: string | null) => comment ?? "—",
			},
			{
				title: copy?.colActions ?? "Actions",
				key: "actions",
				width: 110,
				render: (_: unknown, row) => (
					<Button type="link" danger onClick={() => onDelete(row)}>
						{copy?.delete ?? "Delete"}
					</Button>
				),
			},
		],
		[copy, onDelete]
	);

	return (
		<Table<OrderStatusHistoryRow>
			rowKey="id"
			size="small"
			columns={columns}
			dataSource={rows}
			loading={loading}
			pagination={false}
			locale={{ emptyText: copy?.noRows ?? "No history rows" }}
		/>
	);
};

export default HistoryTable;
